import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { StatusBadge } from "../../../components/ui/StatusBadge";
import { validNextOptions, TRACKING_REQUIRED_STATES } from "../lifecycle";
import { adminOrdersService } from "../../../services/damages";
import { formatApiError } from "../../../services/api";

/**
 * Admin-only "advance status" modal.
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onOpenChange
 * @param {object} props.order  Row object with at minimum id, order_number, status.
 * @param {function} props.onDone  Called with the fresh order after a successful transition.
 */
export function TransitionModal({ open, onOpenChange, order, onDone }) {
  const { t } = useTranslation();
  const [target, setTarget] = useState("");
  const [tracking, setTracking] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const options = order ? validNextOptions(order.status) : [];
  const isTerminal = options.length === 0;

  useEffect(() => {
    if (!open || !order) return;
    setTarget(options[0] || "");
    setTracking("");
    setNotes("");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id]);

  if (!order) return null;

  const requiresTracking = TRACKING_REQUIRED_STATES.has(target);

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return;
    if (requiresTracking && !tracking.trim()) {
      setError(t("admin.transitions.err_tracking_required"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = { new_status: target };
      if (requiresTracking) body.tracking_number = tracking.trim();
      if (notes.trim()) body.notes = notes.trim();
      const fresh = await adminOrdersService.transition(order.id, body);
      toast.success(t("admin.transitions.success"));
      onOpenChange(false);
      onDone && onDone(fresh);
    } catch (err) {
      setError(formatApiError(err, t("common.error_generic")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="transition-modal"
        className="w-full max-w-lg mx-4 sm:mx-auto border-border-subtle bg-surface text-text-primary p-6 sm:p-8 [&_button.absolute]:text-text-secondary"
      >
        <DialogHeader>
          <DialogTitle data-testid="transition-modal-title" className="text-xl">
            {t("admin.transitions.title", {
              order: order.order_number ? `#${order.order_number}` : order.id.slice(0, 6),
            })}
          </DialogTitle>
          <DialogDescription className="text-text-secondary">
            {t("admin.transitions.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="mb-4 flex items-center gap-2 text-sm">
          <span className="text-text-secondary">
            {t("admin.transitions.current_status")}:
          </span>
          <StatusBadge status={order.status} />
        </div>

        {isTerminal ? (
          <div
            className="rounded-md border border-border-subtle bg-offwhite p-4 text-sm text-text-secondary"
            data-testid="transition-terminal"
          >
            {t("admin.transitions.terminal_note")}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label>{t("admin.transitions.new_status_label")}</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger
                  data-testid="transition-target"
                  className="h-10 bg-surface border-border-subtle"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`transition-target-${s}`}
                    >
                      {t(`orders.status.${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {requiresTracking && (
              <div className="space-y-1.5">
                <Label>
                  {t("admin.transitions.tracking_label")}
                  <span className="ml-0.5 text-status-unavailable">*</span>
                </Label>
                <Input
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  data-testid="transition-tracking"
                  placeholder="TRK-…"
                  className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne font-mono text-sm"
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label>{t("admin.transitions.notes_label")}</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="transition-notes"
                rows={2}
                maxLength={500}
                placeholder={t("admin.transitions.notes_placeholder")}
                className="bg-surface border-border-subtle focus-visible:ring-champagne"
              />
            </div>

            {error && (
              <p className="text-sm text-status-unavailable" data-testid="transition-error">
                {error}
              </p>
            )}

            <DialogFooter className="pt-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="transition-cancel"
                className="border-border-subtle text-text-primary hover:bg-offwhite"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={submitting || !target}
                data-testid="transition-submit"
                className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("common.saving")}
                  </>
                ) : (
                  t("admin.transitions.submit_cta")
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
